import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Server,
  Package,
  BookOpen,
  ArrowRight,
  ExternalLink,
  Shield,
  Zap,
  Copy as CopyIcon,
} from 'lucide-react';

// ─── Three developer surfaces (mirror of Slide 10 pitch-deck cards) ─────────
const surfaces = [
  {
    icon: Server,
    accent: 'from-violet-500/20 to-violet-500/5',
    accentBorder: 'border-violet-500/30',
    accentText: 'text-violet-300',
    label: 'Native MCP Server',
    title: 'Desktop AI assistants · AI code editors',
    detail: (
      <>
        Listed in the official Model Context Protocol registry as{' '}
        <code className="text-emerald-400 text-xs font-mono">
          io.github.faulknerwayne73-droid/kojiru-mcp
        </code>
        . Streamable HTTP, Bearer-authenticated, six tools registered.
      </>
    ),
    code: 'discover in desktop AI assistants → enable',
    codes: null,
    href: 'https://registry.modelcontextprotocol.io/v0/servers?search=kojiru',
    testId: 'mcp-surface-registry',
  },
  {
    icon: Package,
    accent: 'from-red-500/20 to-red-500/5',
    accentBorder: 'border-red-500/30',
    accentText: 'text-red-300',
    label: 'npm (TypeScript)',
    title: 'A major U.S. exchange AgentKit · a popular agent framework',
    detail: (
      <>Drop-in ActionProviders for a major U.S. exchange AgentKit and native <code className="text-emerald-400 text-xs font-mono">DynamicStructuredTool</code>s for a popular agent framework / LangGraph. Zod-typed, MCP-backed.</>
    ),
    code: null,
    codes: ['npm i kojiru-agentkit', 'npm i kojiru-langchain'],
    href: 'https://www.npmjs.com/package/kojiru-agentkit',
    testId: 'mcp-surface-npm',
  },
  {
    icon: BookOpen,
    accent: 'from-emerald-500/20 to-emerald-500/5',
    accentBorder: 'border-emerald-500/30',
    accentText: 'text-emerald-300',
    label: 'PyPI (Python)',
    title: 'Popular agent frameworks',
    detail: (
      <>
        The <code className="text-emerald-400 text-xs font-mono">kojiru</code> Python SDK ships with drop-in
        integrations for popular agent frameworks: six credit primitives, one client, one bearer key.
      </>
    ),
    code: 'pip install kojiru[crewai]',
    codes: null,
    href: 'https://pypi.org/project/kojiru/',
    testId: 'mcp-surface-pypi',
  },
];

// ─── Four live proof points ─────────────────────────────────────────────────
const proofPoints = [
  { label: 'MCP Endpoint (live)', value: 'api.kojiru.com/mcp/mcp' },
  { label: 'Tools registered', value: '6 · ACS · stake · slash · draw · anchor · records' },
  { label: 'Slash verified end-to-end (Sepolia bake)', value: '0x694ffe5f…ef1d276' },
  { label: 'On Base Mainnet since', value: '7-20-2026' },
];

function CodeLine({ children }) {
  return (
    <code className="block bg-black/50 border border-white/5 rounded-md px-3 py-2 text-emerald-300 font-mono text-sm">
      {children}
    </code>
  );
}

export default function MCP() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="mcp-page">
      <Helmet>
        <title>MCP + x402 Integration | Kojiru: The Recourse Layer™</title>
        <meta
          name="description"
          content="Kojiru's plug-and-play surface: native MCP server, Coinbase AgentKit + LangChain npm packages, CrewAI PyPI package. One Bearer key. Six credit primitives."
        />
      </Helmet>
      <Navbar />

      {/* ─── HERO ───────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-16 px-6" data-testid="mcp-hero">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-6" data-testid="mcp-hero-badge">
              <Zap className="w-3.5 h-3.5" />
              Plug-and-Play
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
              data-testid="mcp-hero-headline"
            >
              Every MCP tool tells you{' '}
              <em className="not-italic text-gray-400">what happened</em>.
              <br />
              Kojiru is the only MCP tool that binds{' '}
              <em className="not-italic text-emerald-400">stake</em> to{' '}
              <em className="not-italic text-emerald-400">consequence</em>.
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              One Bearer key. Three developer surfaces. Six credit primitives. The Recourse Layer™ ships as a native MCP server, plus companion packages for three popular agent frameworks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── THREE SURFACES ─────────────────────────────────────────────── */}
      <section
        className="py-12 px-6 border-y border-white/5 bg-white/[0.02]"
        data-testid="mcp-surfaces-section"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {surfaces.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.a
                  key={s.testId}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={s.testId}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                  className={`group relative rounded-2xl border ${s.accentBorder} bg-gradient-to-br ${s.accent} p-6 hover:border-white/30 transition-colors flex flex-col`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Icon className={`w-6 h-6 ${s.accentText}`} />
                    <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                  <div
                    className={`text-xs uppercase tracking-[0.15em] font-semibold ${s.accentText} mb-2`}
                  >
                    {s.label}
                  </div>
                  <h3 className="text-xl font-semibold text-white leading-snug mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed mb-4 flex-1">
                    {s.detail}
                  </p>
                  <div className="space-y-2">
                    {s.code && <CodeLine>{s.code}</CodeLine>}
                    {s.codes &&
                      s.codes.map((c) => <CodeLine key={c}>{c}</CodeLine>)}
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── X402 COMPARISON STRIP ──────────────────────────────────────── */}
      <section className="py-16 px-6" data-testid="mcp-x402-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs uppercase tracking-widest text-emerald-300 mb-4">
              <Shield className="w-3.5 h-3.5" />
              The category shift
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              x402 proves the agent paid. Kojiru proves what was authorized and who stood behind it.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="mcp-x402-grid">
            <div
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
              data-testid="mcp-x402-then"
            >
              <div className="text-xs uppercase tracking-[0.15em] text-gray-500 font-semibold mb-3">
                Where every other stack stops
              </div>
              <div className="text-xl text-white mb-3 leading-snug">
                <code className="bg-white/5 border border-white/10 px-2 py-0.5 rounded text-emerald-300 font-mono text-base">
                  x402
                </code>{' '}
                receipt →{' '}
                <span className="italic text-gray-400 font-medium">
                  &ldquo;the agent paid&rdquo;
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                A delivery confirmation. Not a remedy. No pledge has been slashed.
              </p>
            </div>

            <div
              className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/10 to-red-500/[0.02] p-6"
              data-testid="mcp-x402-now"
            >
              <div className="text-xs uppercase tracking-[0.15em] text-red-300 font-semibold mb-3">
                Where Kojiru starts
              </div>
              <div className="text-xl text-white mb-3 leading-snug">
                <code className="bg-white/5 border border-red-500/30 px-2 py-0.5 rounded text-red-300 font-mono text-base">
                  KojiruACSHook.slash()
                </code>{' '}
                →{' '}
                <span className="italic text-emerald-300 font-semibold">
                  &ldquo;the breach recorded next to the public pledge&rdquo;
                </span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Breach recorded in the{' '}
                <strong className="text-red-300">same transaction</strong> as the proof. Evidence, not a payout. Not insurance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── LIVE PROOF STRIP ───────────────────────────────────────────── */}
      <section
        className="py-12 px-6 border-y border-white/5"
        data-testid="mcp-proof-section"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1830] to-[#050b12] border border-white/10 p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {proofPoints.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  viewport={{ once: true }}
                  data-testid={`mcp-proof-item-${i}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300 font-semibold mb-2 leading-tight">
                    {p.label}
                  </div>
                  <div className="text-white font-mono text-sm leading-snug break-words">
                    {p.value}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6" data-testid="mcp-cta-section">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
              One Bearer key. Three surfaces. Six primitives.
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Request an API key, read the tool reference, or install a package. All three paths land you in the same place: your agent talking to the live Kojiru MCP server on Base mainnet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://docs.kojiru.com/getting-started/"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="mcp-cta-getting-started"
              >
                <Button
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2"
                >
                  Read the Docs
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <Link to="/contact" data-testid="mcp-cta-api-key">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 gap-2"
                >
                  Request an API Key
                </Button>
              </Link>
              <a
                href="https://docs.kojiru.com/mcp/tools/"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="mcp-cta-tool-reference"
              >
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-white hover:bg-white/5 gap-2"
                >
                  Tool Reference
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
